import { VersionLib, VersionStr } from "../../../logic/Versions"
import { Page } from "../../types"

type ClientCSharpAssemblyReferenceProps = {
    os: string
    isContainer: boolean
}

const libraries : Record<string, string> = {
    'windows': `gurobi${VersionLib}.lib`,
    'linux': `libgurobi${VersionLib}.so`,
    'macos': `libgurobi${VersionLib}.dylib`
}

const getLibrary = (os: string, isContainer: boolean) => isContainer ? libraries['linux'] : libraries[os]

const CSPROJ = (os: string, isContainer: boolean) => {
    const library = getLibrary(os, isContainer)
    const xml = 
`<ItemGroup>
    <Reference Include="gurobi${VersionLib}.netstandard20">
        <HintPath>gurobi${VersionLib}.netstandard20.dll</HintPath>
    </Reference>
</ItemGroup>

<ItemGroup>
    <None Update="${library}">
        <CopyToOutputDirectory>PreserveNewest</CopyToOutputDirectory>
    </None>
</ItemGroup>`
    return <>
        <h3>Using an editor like VSCode</h3>
        <p>
            <ul>
                <li>Open your project file (with extension <code>csproj</code>) in the editor</li>
                <li>Add the lines below.</li>
            </ul>            
        </p>
        <pre>{xml}</pre>
    </>
}

const VSWIN = (library: string) => <>
    <h3>Using Visual Studio 2022 for Windows</h3>
    <p>
        <ul>
            <li>Right-click your project in the Solution Explorer.</li>
            <li>Click "Add" and then "Project reference..."</li>
            <li>In the dialog, click the "Browse..." button at the bottom.</li>
            <li>Select the <code>gurobi{VersionLib}.netstandard20.dll</code> file and click OK.</li>
            <li>In the Solution Explorer, right-click <code>{library}</code></li>
            <li>Choose "Properties"</li>
            <li>For "Copy to Output Directory", choose "Copy if newer"</li>
        </ul>
    </p>
</>

const VSMAC = (library: string) => <>
    <h3>Using Visual Studio 2022 for MacOS</h3>
    <p>
        <ul>
            <li>Right-click your project in the Solution Explorer.</li>
            <li>Click "Add" and then "Reference..."</li>
            <li>In the dialog, go to the ".NET Assembly" tab page</li>
            <li>Then click the "Browse..." button at the bottom.</li>
            <li>Select the <code>gurobi{VersionLib}.netstandard20.dll</code> file and click OK.</li>
            <li>In the Solution Explorer, right-click <code>{library}</code></li>
            <li>Choose "Quick Properties" and then "Copy to Output Directory"</li>
        </ul>
    </p>
</>

const render = ({ os, isContainer } : ClientCSharpAssemblyReferenceProps) => {
    const library = getLibrary(os, isContainer)
    return <>
        <p>Gurobi ships with a DLL file that must be referenced by your project in order to use the Gurobi API. You can 
            find the file <code>gurobi{VersionLib}.netstandard20.dll</code> in the <code>lib</code> folder of your Gurobi 
            installation.
            {isContainer ? <> For deployment inside a Docker image, you will also need the main Gurobi library <code>{library}</code> for Linux.
                To retrieve this file, download the Gurobi Linux distribution from our website and extract the archive to a temporary
                folder. You can find the library in the <code>lib</code> folder as <code>libgurobi.so.{VersionStr}</code>. Copy both library files to your application folder.
                and rename <code>libgurobi.so.{VersionStr}</code> to <code>libgurobi{VersionLib}.so</code>. </> 
                : <> For deployment to another machine, you will also need the main Gurobi library <code>{library}</code>,
                which can be found in the same <code>lib</code> folder. Copy both library files to your application folder.</>
            }
             </p>
        {os == 'windows' && VSWIN(library)}
        {os == 'macos' && VSMAC(library)}
        {CSPROJ(os, isContainer)}        
    </>
}

const ClientCSharpAssemblyReference : Page<ClientCSharpAssemblyReferenceProps> = { key: 'client-csharp-assembly-reference', title: 'Add Gurobi assembly reference', render }

export default ClientCSharpAssemblyReference